if (!customElements.get('stessia-length-advisor')) {
  customElements.define('stessia-length-advisor', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.heightInput = this.querySelector('.stessia-length__height-range');
      this.heightOutput = this.querySelector('[data-height-output]');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
      this.heightInput?.addEventListener('input', () => this.updateHeightResult());
      this.updateHeightResult();
    }

    select(index) {
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
      this.updateHeightResult();
    }

    updateHeightResult() {
      const height = Number(this.heightInput?.value || 170);
      const shortMax = Number(this.dataset.heightShortMax || 164);
      const averageMax = Number(this.dataset.heightAverageMax || 175);
      const heightCategory = height <= shortMax ? 'short' : height <= averageMax ? 'average' : 'tall';
      const panel = this.panels?.find(item => !item.hidden) || this.panels?.[0];
      const result = panel?.querySelector('[data-height-result]');
      if (!panel) return;

      if (this.heightOutput) this.heightOutput.textContent = `${height} cm`;
      if (this.heightInput) {
        const min = Number(this.heightInput.min || this.dataset.heightMin || 150);
        const max = Number(this.heightInput.max || this.dataset.heightMax || 195);
        const progress = max > min ? ((height - min) / (max - min)) * 100 : 0;
        this.heightInput.style.setProperty('--range-progress', `${progress}%`);
      }

      panel.querySelectorAll('[data-height-image]').forEach(image => {
        image.hidden = image.dataset.heightImage !== heightCategory;
      });

      const reference = panel.querySelector('[data-reference-height]');
      if (reference) reference.textContent = `Guide for ${height} cm`;

      const length = Number(panel.dataset.lengthCm || 0);
      const calculation = panel.querySelector('[data-height-calculation]');
      if (calculation && length && height) {
        const ratio = (length / height * 100).toLocaleString('de-DE', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        });
        calculation.textContent = `At ${height} cm body height, ${length} cm equals about ${ratio} % of your height.`;
      }

      if (result) result.textContent = panel.dataset[`${heightCategory}Result`] || panel.dataset.averageResult || '';
    }

    onKeydown(event, index) {
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }
  });
}

if (!customElements.get('stessia-quantity-advisor')) {
  customElements.define('stessia-quantity-advisor', class extends HTMLElement {
    connectedCallback() {
      this.questions = [...this.querySelectorAll('fieldset')];
      this.currentIndex = 0;
      this.completed = false;
      this.result = this.querySelector('[data-quantity-result]');
      this.placeholder = this.querySelector('[data-quantity-placeholder]');
      this.progressText = this.querySelector('[data-progress-text]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.quantity = this.querySelector('[data-result-quantity]');
      this.weight = this.querySelector('[data-result-weight]');
      this.resultTitle = this.querySelector('[data-result-title]');
      this.resultText = this.querySelector('[data-result-text]');
      this.remainingText = this.querySelector('[data-remaining-text]');
      this.backButton = this.querySelector('[data-quantity-back]');
      this.nextButton = this.querySelector('[data-quantity-next]');
      this.nextLabel = this.querySelector('[data-next-label]');
      this.defaultNextLabel = this.nextLabel?.textContent || 'Next';
      this.resultButtonLabel = this.dataset.resultButtonLabel || 'Show recommendation';
      this.querySelectorAll('input[type="radio"]').forEach(input => {
        input.addEventListener('change', () => {
          this.completed = false;
          this.update();
        });
      });
      this.backButton?.addEventListener('click', () => this.goBack());
      this.nextButton?.addEventListener('click', () => this.goNext());
      this.showQuestion(0, false);
      this.update();
    }

    showQuestion(index, moveFocus = true) {
      this.currentIndex = Math.max(0, Math.min(index, this.questions.length - 1));
      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== this.currentIndex;
      });
      if (moveFocus) {
        const legend = this.questions[this.currentIndex]?.querySelector('legend');
        legend?.setAttribute('tabindex', '-1');
        legend?.focus({ preventScroll: true });
      }
      this.update();
    }

    goBack() {
      if (this.currentIndex === 0) return;
      this.completed = false;
      this.showQuestion(this.currentIndex - 1);
    }

    goNext() {
      const currentAnswer = this.questions[this.currentIndex]?.querySelector('input:checked');
      if (!currentAnswer) return;
      if (this.currentIndex < this.questions.length - 1) {
        this.showQuestion(this.currentIndex + 1);
        return;
      }
      this.completed = true;
      this.update();
      this.result?.setAttribute('tabindex', '-1');
      this.result?.focus({ preventScroll: true });
    }

    update() {
      const checked = this.questions.map(question => question.querySelector('input:checked')).filter(Boolean);
      const answered = checked.length;
      const total = this.questions.length;
      const progress = total ? Math.round(answered / total * 100) : 0;
      this.progressText.textContent = `${answered} of ${total} answered`;
      this.progressBar.style.setProperty('--progress', `${progress}%`);

      const currentAnswered = Boolean(this.questions[this.currentIndex]?.querySelector('input:checked'));
      if (this.backButton) this.backButton.disabled = this.currentIndex === 0;
      if (this.nextButton) this.nextButton.disabled = !currentAnswered;
      if (this.nextLabel) {
        this.nextLabel.textContent = this.currentIndex === total - 1 ? this.resultButtonLabel : this.defaultNextLabel;
      }

      const remaining = Math.max(0, total - answered);
      if (this.remainingText) {
        if (remaining === 0) this.remainingText.textContent = 'All questions are answered. Now show your recommendation.';
        else this.remainingText.textContent = remaining === 1 ? '1 more answer until your recommendation.' : `${remaining} more answers until your recommendation.`;
      }

      if (!total || answered !== total || !this.completed) {
        this.result.hidden = true;
        this.placeholder.hidden = false;
        return;
      }

      const score = checked.reduce((sum, input) => sum + Number(input.dataset.points || 0), 0);
      const minimumQuantity = checked.reduce((minimum, input) => Math.max(minimum, Number(input.dataset.minQuantity || 1)), 1);
      const medium = Number(this.dataset.mediumThreshold || 4);
      const high = Number(this.dataset.highThreshold || 7);
      let level = 'low';
      if (score >= high) level = 'high';
      else if (score >= medium) level = 'medium';

      const levelOrder = { low: 1, medium: 2, high: 3 };
      if (minimumQuantity > levelOrder[level]) {
        level = minimumQuantity >= 3 ? 'high' : 'medium';
      }

      this.quantity.textContent = this.dataset[`${level}Quantity`] || '';
      if (this.weight) this.weight.textContent = this.dataset[`${level}Weight`] || '';
      this.resultTitle.textContent = this.dataset[`${level}Title`] || '';
      this.resultText.textContent = this.dataset[`${level}Text`] || '';
      this.placeholder.hidden = true;
      this.result.hidden = false;
    }
  });
}

if (!customElements.get('stessia-color-library')) {
  customElements.define('stessia-color-library', class extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('[data-color-source]');
      try {
        this.colors = JSON.parse(source.textContent).colors || [];
      } catch (error) {
        this.colors = [];
      }
      this.activeCategory = 'all';
      this.query = '';
      this.grid = this.querySelector('[data-color-grid]');
      this.count = this.querySelector('[data-color-count]');
      this.search = this.querySelector('[data-color-search]');
      this.filters = [...this.querySelectorAll('[data-color-filter]')];
      this.filters.forEach(filter => filter.addEventListener('click', () => {
        this.activeCategory = filter.dataset.colorFilter;
        this.filters.forEach(item => item.setAttribute('aria-pressed', String(item === filter)));
        this.render();
      }));
      this.search.addEventListener('input', () => {
        this.query = this.search.value.trim().toLowerCase();
        this.render();
      });
      this.render();
    }

    render() {
      const matches = this.colors.filter(color => {
        const category = String(color.category || '').toLowerCase();
        const categoryMatch = this.activeCategory === 'all' || category === this.activeCategory;
        const haystack = `${color.code || ''} ${color.variantLabel || ''} ${color.name || ''}`.toLowerCase();
        return categoryMatch && (!this.query || haystack.includes(this.query));
      });
      this.grid.replaceChildren(...matches.map(color => this.createCard(color)));
      if (!matches.length) {
        const empty = document.createElement('p');
        empty.className = 'stessia-colors__empty';
        empty.textContent = this.dataset.emptyText || 'No matching color found.';
        this.grid.append(empty);
      }
      this.count.textContent = `${matches.length} colors`;
    }

    createCard(color) {
      const article = document.createElement('article');
      article.className = 'stessia-color-card';
      const media = document.createElement('div');
      media.className = 'stessia-color-card__media';
      const image = document.createElement('img');
      image.src = color.image;
      image.alt = `${color.variantLabel || color.code} – ${color.name}`;
      image.loading = 'lazy';
      image.width = 720;
      image.height = 720;
      const category = document.createElement('span');
      category.className = 'stessia-color-card__category';
      category.textContent = color.category;
      media.append(image, category);
      const copy = document.createElement('div');
      copy.className = 'stessia-color-card__copy';
      const code = document.createElement('span');
      code.className = 'stessia-color-card__code heading';
      code.textContent = color.variantLabel || color.code;
      const name = document.createElement('span');
      name.className = 'stessia-color-card__name';
      name.textContent = color.name;
      copy.append(code, name);
      article.append(media, copy);
      return article;
    }
  });
}
